import { useEffect, useMemo, useState } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";
import "./ExportExcelQualite.css";

const MODES = [
  { id: "LIST", label: "Liste IDS" },
  { id: "RANGE", label: "Plage" },
  { id: "ALL", label: "Tous" },
  { id: "DATE", label: "Par date" },
  { id: "BANC", label: "Banc pur" },
];

const STACK_DATE_FIELDS = [
  { id: "QualiteStack", label: "Contrôle Qualité Stack" },
  { id: "Impregnation", label: "Imprégnation" },
  { id: "Empilement", label: "Empilement" },
];

const LEVEL_ORDER = ["stack", "pli", "eprouvette", "ilss"];

export default function ExportExcelQualite() {
  const { t } = useI18n();
  const [catalog, setCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [err, setErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState("");

  const [params, setParams] = useState([]);
  const [stackIdMode, setStackIdMode] = useState("LIST");
  const [stackIdsText, setStackIdsText] = useState("");
  const [stackIdStart, setStackIdStart] = useState("");
  const [stackIdEnd, setStackIdEnd] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [stackDateField, setStackDateField] = useState("QualiteStack");

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/qualite/export-excel/catalog");
        const j = await r.json();
        if (j.status !== "success") throw new Error(j.message || "Catalogue indisponible");
        setCatalog(j);
      } catch (e) {
        setErr(e.message || "Erreur chargement catalogue");
      } finally {
        setLoadingCatalog(false);
      }
    })();
  }, []);

  const allParamKeys = useMemo(() => {
    if (!catalog?.params) return [];
    return LEVEL_ORDER.flatMap((level) => (catalog.params[level] || []).map((p) => p.key));
  }, [catalog]);

  const toggleParam = (key) => {
    setParams((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAllVisible = () => {
    const visible = filteredLevels.flatMap((g) => g.items.map((p) => p.key));
    setParams((prev) => [...new Set([...prev, ...visible])]);
  };

  const clearAll = () => setParams([]);

  const hasPliParam = useMemo(() => {
    if (!catalog?.params?.pli) return false;
    const pliKeys = new Set(catalog.params.pli.map((p) => p.key));
    return params.some((k) => pliKeys.has(k));
  }, [catalog, params]);

  const filteredLevels = useMemo(() => {
    if (!catalog?.params) return [];
    const q = filter.trim().toLowerCase();
    return LEVEL_ORDER.map((level) => ({
      level,
      title: catalog.levels?.[level] || level,
      items: (catalog.params[level] || []).filter(
        (p) =>
          !q ||
          p.key.toLowerCase().includes(q) ||
          p.label.toLowerCase().includes(q) ||
          (p.table || "").toLowerCase().includes(q)
      ),
    })).filter((g) => g.items.length > 0);
  }, [catalog, filter]);

  const buildPayload = () => {
    const stack_ids = stackIdsText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      params,
      stack_id_mode: stackIdMode,
      stack_ids,
      stack_id_start: stackIdStart ? Number(stackIdStart) : 0,
      stack_id_end: stackIdEnd ? Number(stackIdEnd) : 0,
      date_start: dateStart,
      date_end: dateEnd,
      stack_date_field: stackDateField,
    };
  };

  const handleExport = async () => {
    if (!params.length) {
      setErr("Sélectionnez au moins un paramètre.");
      return;
    }
    setErr("");
    setExporting(true);
    try {
      const r = await apiFetch("/api/qualite/export-excel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.message || `Erreur export (${r.status})`);
      }
      const blob = await r.blob();
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="?([^";]+)"?/i);
      const filename = m?.[1] || "export_qualite.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || t("messages.excelError", "Impossible de générer le fichier Excel."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <MenuBar active="qualite" />
      <div className="export-page">
        <div className="export-wrap">
          <header className="export-hero">
            <p className="export-kicker">Qualité · Export</p>
            <h1 className="export-title">{t("menu.items.export_excel", "Export Excel")}</h1>
            <p className="export-sub">
              Export personnalisé multi-tables — stack, pli, éprouvette, ILSS. Sélectionnez les colonnes et la plage de données.
            </p>
          </header>

          {!loadingCatalog && (
            <div className="export-stats">
              <div className="export-stat">
                <div className="export-stat-val">{params.length}</div>
                <div className="export-stat-lab">Paramètres</div>
              </div>
              <div className="export-stat">
                <div className="export-stat-val">{allParamKeys.length}</div>
                <div className="export-stat-lab">Disponibles</div>
              </div>
              <div className="export-stat">
                <div className="export-stat-val">{stackIdMode}</div>
                <div className="export-stat-lab">Mode</div>
              </div>
            </div>
          )}

          {err && <div className="export-alert error">{err}</div>}

          {loadingCatalog ? (
            <p className="export-loading">Chargement du catalogue…</p>
          ) : (
            <>
              <section className="export-card">
                <div className="export-card-head">
                  <h2 className="export-card-title">1 · Paramètres</h2>
                  <div className="export-card-actions">
                    <button type="button" className="export-btn-ghost" onClick={selectAllVisible}>
                      Tout (filtre)
                    </button>
                    <button type="button" className="export-btn-ghost" onClick={clearAll}>
                      Effacer
                    </button>
                  </div>
                </div>
                <label className="export-field">
                  <span className="export-label">Rechercher</span>
                  <input
                    className="export-input"
                    placeholder="Nom, table, libellé…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </label>
                <div className="export-catalog">
                  {filteredLevels.map(({ level, title, items }) => (
                    <div key={level} className="export-level">
                      <div className="export-level-title">{title}</div>
                      {items.map((p) => {
                        const on = params.includes(p.key);
                        return (
                          <label
                            key={p.key}
                            className={`export-check${on ? " on" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleParam(p.key)}
                            />
                            <span>
                              <span className="export-param-key">{p.key}</span>
                              <span className="export-param-label"> — {p.label}</span>
                              {p.table && <span className="export-tag">{p.table}</span>}
                              {p.banc_ok && <span className="export-tag export-tag-banc">BANC</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </section>

              <section className="export-card">
                <h2 className="export-card-title">2 · Périmètre</h2>
                <div className="export-mode-pills">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`export-pill${stackIdMode === m.id ? " on" : ""}`}
                      onClick={() => setStackIdMode(m.id)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {stackIdMode === "LIST" && (
                  <label className="export-field">
                    <span className="export-label">IDs stack</span>
                    <textarea
                      className="export-textarea"
                      rows={3}
                      placeholder="IDS101523 IDS101524"
                      value={stackIdsText}
                      onChange={(e) => setStackIdsText(e.target.value)}
                    />
                  </label>
                )}

                {stackIdMode === "RANGE" && (
                  <div className="export-row">
                    <label className="export-field">
                      <span className="export-label">Début</span>
                      <input
                        className="export-input"
                        type="number"
                        value={stackIdStart}
                        onChange={(e) => setStackIdStart(e.target.value)}
                      />
                    </label>
                    <label className="export-field">
                      <span className="export-label">Fin</span>
                      <input
                        className="export-input"
                        type="number"
                        value={stackIdEnd}
                        onChange={(e) => setStackIdEnd(e.target.value)}
                      />
                    </label>
                  </div>
                )}

                {(stackIdMode === "DATE" || stackIdMode === "BANC") && (
                  <div className="export-row">
                    <label className="export-field">
                      <span className="export-label">Date début</span>
                      <input
                        className="export-input"
                        type="date"
                        value={dateStart}
                        onChange={(e) => setDateStart(e.target.value)}
                      />
                    </label>
                    <label className="export-field">
                      <span className="export-label">Date fin</span>
                      <input
                        className="export-input"
                        type="date"
                        value={dateEnd}
                        onChange={(e) => setDateEnd(e.target.value)}
                      />
                    </label>
                  </div>
                )}

                {stackIdMode === "DATE" && !hasPliParam && (
                  <label className="export-field">
                    <span className="export-label">Date stack par</span>
                    <select
                      className="export-select"
                      value={stackDateField}
                      onChange={(e) => setStackDateField(e.target.value)}
                    >
                      {STACK_DATE_FIELDS.map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </label>
                )}

                {stackIdMode === "ALL" && (
                  <div className="export-alert warn">
                    Export de tous les stacks — peut être long avec des paramètres « pli ».
                  </div>
                )}

                {stackIdMode === "BANC" && (
                  <p className="export-alert info">
                    Mode banc : paramètres pli Banc_de_test / Woodoo_V1 uniquement.
                  </p>
                )}
              </section>

              <div className="export-footer">
                <span className="export-sub">
                  {params.length} colonne(s) · mode {stackIdMode}
                </span>
                <button
                  type="button"
                  className="export-btn-primary"
                  disabled={exporting || !params.length}
                  onClick={handleExport}
                >
                  {exporting ? "Génération en cours…" : "Exporter Excel"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
